import { Icon } from '@/shared/components/ui/icon';
import { cn } from '@/shared/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

/**
 * Figma 라이브러리 Breadcrumbs (Group) (744:7795) · Breadcrumbs (Item) (744:7777)
 * - 항목: 높이 24 · 좌우 8 · radius 8 · 12 SemiBold
 *   기본 grey-500 · hover grey-100 배경 + grey-700 · focus grey-200 배경 + 검정 · 현재(마지막) 검정 · disabled grey-300
 * - 사이 구분: nav-arrow-right 14 (grey-400)
 * - size="medium": 화면 오버라이드(M-09 789:8001) — 14 SemiBold, 글자 좌우 8 추가
 *
 * 사용: <Breadcrumbs items={[{ label: '실적', to: … }, { label: '2026년 7월', to: … }, { label: '김정도이비인후과의원' }]} />
 * 링크는 renderLink로 라우터 Link를 넘긴다 (asChild 방식).
 */
export interface BreadcrumbItem {
  label: React.ReactNode;
  /** 있으면 링크로, 없으면 글자로 그린다 */
  href?: string;
  disabled?: boolean;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  size?: 'small' | 'medium';
  /** 링크 항목 렌더러 — 예: (item, children) => <Link to={item.href}>{children}</Link> */
  renderLink?: (
    item: BreadcrumbItem,
    children: React.ReactNode,
  ) => React.ReactElement;
}

const itemClass = (size: 'small' | 'medium') =>
  cn(
    'inline-flex h-6 items-center justify-center rounded-8 px-2 py-1 whitespace-nowrap text-fg-tertiary outline-none transition-colors',
    'hover:bg-grey-100 hover:text-fg-secondary focus-visible:bg-grey-200 focus-visible:text-fg-primary',
    'aria-[current=page]:text-fg-primary aria-[current=page]:hover:bg-transparent aria-disabled:pointer-events-none aria-disabled:text-fg-disabled',
    size === 'small' ? 'text-button-12-sb' : 'text-button-14-sb [&>span]:px-2',
  );

export const Breadcrumbs = ({
  items,
  size = 'small',
  renderLink,
  className,
  ...props
}: BreadcrumbsProps) => (
  <nav aria-label="현재 위치" className={className} {...props}>
    <ol className="flex flex-wrap items-center">
      {items.map((item, i) => {
        const last = i === items.length - 1;
        const content = <span>{item.label}</span>;
        const common = {
          className: itemClass(size),
          'aria-current': last ? ('page' as const) : undefined,
          'aria-disabled': item.disabled || undefined,
        };
        return (
          <li key={i} className="flex items-center">
            {item.href && !last ? (
              renderLink ? (
                <Slot {...common}>{renderLink(item, content)}</Slot>
              ) : (
                <a href={item.href} {...common}>
                  {content}
                </a>
              )
            ) : (
              <span {...common}>{content}</span>
            )}
            {!last && (
              <Icon name="nav-arrow-right-line-14" className="text-fg-light" />
            )}
          </li>
        );
      })}
    </ol>
  </nav>
);
